import {
  initVersionedSchema,
  toMentionContent,
  toParagraphContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IGetRecordResponse,
  IHasSourceIdentifier,
  ITranslationMap,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Interaction, toMention } from '@principle-theorem/principle-core';
import {
  IInteractionV2,
  InteractionType,
  MentionResourceType,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithPrimaryKey,
  getError,
  snapshotCombineLatest,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, upperFirst } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientInteractionDestinationEntity,
  IBasePatientInteractionJobData,
  IPatientInteractionMigrationData,
} from '../../../destination/entities/patient-interactions';
import { type TranslationMapHandler } from '../../../translation-map';
import { PatientAlertSourceEntity } from '../../source/entities/patient-alerts';
import {
  CorePracticePatientCommunication,
  CorePracticePatientCommunicationStatus,
  PatientCommunicationSourceEntity,
} from '../../source/entities/patient-communication';
import { PatientInteractionSourceEntity } from '../../source/entities/patient-interactions';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';

interface IPatientInteractionJobData
  extends IBasePatientInteractionJobData<CorePracticePatient> {
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
}

export class PatientInteractionDestinationEntity extends BasePatientInteractionDestinationEntity<
  CorePracticePatient,
  IPatientInteractionJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientInteractions: new PatientInteractionSourceEntity(),
    patientAlerts: new PatientAlertSourceEntity(),
    patientCommunication: new PatientCommunicationSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInteractionJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMapHandler),
      translationMapHandler.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([staff$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [staff]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          staff,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInteractionJobData
  ): Promise<
    | IPatientInteractionMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const sourcePatientNumber = data.sourcePatient.data.patientNumber;
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const sourceInteractions =
        await this.sourceEntities.patientInteractions.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const interactions: (IInteractionV2 & IHasSourceIdentifier)[] =
        sourceInteractions.map((interaction) => ({
          sourceIdentifier: interaction.record.uid,
          ...Interaction.init({
            type: InteractionType.Note,
            title: [toTextContent(`Added a note`)],
            createdAt: toTimestamp(moment.utc(interaction.data.createDate)),
            content: initVersionedSchema(interaction.data.note || undefined),
          }),
        }));

      const sourceAlerts = (
        await this.sourceEntities.patientAlerts.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        )
      ).filter((alert) => {
        if (alert.data.isDeleted) {
          return false;
        }
        if (!alert.data.endDateUtc) {
          return true;
        }

        const endDate = moment.utc(alert.data.endDateUtc);
        return endDate.isAfter(moment());
      });

      const pinnedInteractions: (IInteractionV2 & IHasSourceIdentifier)[] =
        sourceAlerts.map((interaction) => ({
          sourceIdentifier: interaction.record.uid,
          ...Interaction.init({
            type: InteractionType.Note,
            title: [toTextContent(`Added a note`)],
            pinned: true,
            createdAt: toTimestamp(moment.utc(interaction.data.startDateUtc)),
            content: initVersionedSchema(interaction.data.description),
          }),
        }));

      const sourcePatientCommunications =
        await this.sourceEntities.patientCommunication.filterRecords(
          migration,
          'patientNumber',
          sourcePatientNumber
        );

      const communicationInteractions =
        this._getPatientCommunicationInteractions(
          sourcePatientCommunications,
          await Firestore.getDoc(patientRef)
        );

      return {
        patientRef,
        interactions: [
          ...interactions,
          ...pinnedInteractions,
          ...communicationInteractions,
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }
  private _getPatientCommunicationInteractions(
    sourcePatientCommunications: IGetRecordResponse<CorePracticePatientCommunication>[],
    patient: WithRef<IPatient>
  ): (IInteractionV2 & IHasSourceIdentifier)[] {
    return compact(
      sourcePatientCommunications.map((communication) => {
        if (
          [
            CorePracticePatientCommunicationStatus.Error,
            CorePracticePatientCommunicationStatus.Failed,
            CorePracticePatientCommunicationStatus.Queued,
            CorePracticePatientCommunicationStatus.Undelivered,
            CorePracticePatientCommunicationStatus.Pending,
          ].includes(communication.data.status)
        ) {
          return;
        }

        const interactionType = communication.data.method;
        const isResponse = communication.data.direction === 'Inbound';
        const type = isResponse
          ? InteractionType.SmsReceived
          : InteractionType.Sms;
        const title = isResponse
          ? [
              toMentionContent(toMention(patient, MentionResourceType.Patient)),
              toTextContent(` replied via ${upperFirst(interactionType)}`),
            ]
          : [
              toTextContent(`${upperFirst(interactionType)} sent to `),
              toMentionContent(toMention(patient, MentionResourceType.Patient)),
            ];
        return {
          sourceIdentifier: communication.record.uid,
          ...Interaction.init({
            type,
            title,
            createdAt: communication.record.translations.date,
            content: initVersionedSchema(
              compact([
                communication.data.body
                  ? toParagraphContent(communication.data.body)
                  : undefined,
              ])
            ),
          }),
        };
      })
    );
  }
}
